// Full screen: frame the camera by hand. The whole picture at t, with the camera's frame drawn on it as a box: drag
// the box to move it, drag its corner (or pinch, scroll or slide) to make it bigger or smaller. Opened from every
// place a camera framing is chosen. Back keeps the frame as it is (`onDone`); undo takes it back.
import { useRef, useState, type PointerEvent, type WheelEvent } from "react";
import { MAX_ZOOM, WHOLE } from "../../../engine/camera";
import type { Camera } from "../../../model/episode";
import type { TimelineEpisode } from "../../../model/timeline";
import { FrameSvg } from "../../../render/FrameSvg";
import { DoneFooter, FitFrame, Icon, OnTop, Page, Slider, TopBar, useIdleClock } from "../../common";
import { frameWith, subLine } from "./shared";

// Kept to where the view can actually go, so the box never hangs off the picture.
function keep(c: Camera): Camera {
  const zoom = Math.max(1, Math.min(MAX_ZOOM, c.zoom)), half = 0.5 / zoom;
  const fit = (v: number) => Math.max(half, Math.min(1 - half, v));
  return { cx: fit(c.cx), cy: fit(c.cy), zoom };
}

export function FreeFramePage({ ep, t, initial, onDone }: {
  ep: TimelineEpisode; t: number; initial: Camera; onDone: (camera: Camera) => void;
}) {
  const clock = useIdleClock();
  // Starting from the whole picture would leave nothing to move, so the box starts a little in.
  const [cam, setCam] = useState<Camera>(() => keep(initial.zoom > 1.01 ? initial : { ...initial, zoom: 1.6 }));
  const camNow = useRef(cam);
  camNow.current = cam;
  const stage = useRef<HTMLDivElement>(null);
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const start = useRef<{ mode: "move" | "size"; x: number; y: number; cam: Camera; dist: number } | null>(null);

  // A point on screen as a fraction of the picture (0..1 across and down).
  const at = (p: { x: number; y: number }) => {
    const r = stage.current!.getBoundingClientRect();
    return { x: (p.x - r.left) / r.width, y: (p.y - r.top) / r.height };
  };
  const spread = () => {
    const [a, b] = [...pointers.current.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  };
  const centre = () => {
    const ps = [...pointers.current.values()];
    return { x: ps.reduce((n, p) => n + p.x, 0) / ps.length, y: ps.reduce((n, p) => n + p.y, 0) / ps.length };
  };
  function begin(mode: "move" | "size", c: Camera) {
    start.current = { mode, ...centre(), cam: c, dist: spread() };
  }
  function down(e: PointerEvent<HTMLDivElement>) {
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const handle = (e.target as HTMLElement).closest(".ff-handle");
    begin(handle && pointers.current.size === 1 ? "size" : "move", camNow.current);
  }
  function move(e: PointerEvent<HTMLDivElement>) {
    if (!pointers.current.has(e.pointerId) || !start.current) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const s = start.current, r = stage.current!.getBoundingClientRect();
    if (s.mode === "size") {
      // The corner follows the finger; the box keeps its centre and its shape.
      const p = at({ x: e.clientX, y: e.clientY });
      const half = Math.max(Math.abs(p.x - s.cam.cx), Math.abs(p.y - s.cam.cy), 0.5 / MAX_ZOOM);
      setCam(keep({ ...s.cam, zoom: 0.5 / half }));
      return;
    }
    const c = centre();
    const zoom = s.dist > 0 && pointers.current.size > 1 ? s.cam.zoom / (spread() / s.dist) : s.cam.zoom; // spreading fingers makes the box bigger
    setCam(keep({ cx: s.cam.cx + (c.x - s.x) / r.width, cy: s.cam.cy + (c.y - s.y) / r.height, zoom }));
  }
  function up(e: PointerEvent<HTMLDivElement>) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size) begin("move", camNow.current); else start.current = null;
  }
  const wheel = (e: WheelEvent<HTMLDivElement>) => setCam(c => keep({ ...c, zoom: c.zoom * (1 - e.deltaY * 0.0015) }));

  const w = 100 / cam.zoom;
  const box = { left: `${(cam.cx - 0.5 / cam.zoom) * 100}%`, top: `${(cam.cy - 0.5 / cam.zoom) * 100}%`, width: `${w}%`, height: `${w}%` };
  // Opened from inside another page, so it goes straight on top of the screen rather than inside that page.
  return (
    <OnTop><div className="layer">
      <Page dark className="framer">
        <TopBar onBack={() => onDone(cam)} title="Frame it yourself" sub={subLine(t, false)} />
        <FitFrame frameRef={stage} className="editing" onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} onWheel={wheel}>
          <FrameSvg id="tl-freeframe" boil={false} frame={frameWith(ep, t, clock, WHOLE)} />
          <div className="ff-box" style={box}><span className="ff-handle" aria-hidden /></div>
        </FitFrame>
        <div className="face-tools">
          <div className="ff-tools">
            <div className="frame-box ff-peek"><FrameSvg id="tl-freeframe-peek" boil={false} frame={frameWith(ep, t, clock, cam)} /></div>
            <div className="ff-side">
              <p className="fs-hint">Drag the box to move the camera. Drag its corner, pinch or slide to change how close it is.</p>
              <Slider label="Zoom" min={1} max={MAX_ZOOM} step={0.05} value={cam.zoom} onChange={zoom => setCam(c => keep({ ...c, zoom }))} show={v => (v <= 1.01 ? "Whole picture" : `${v.toFixed(1)}×`)} />
            </div>
          </div>
        </div>
        <DoneFooter onDone={() => onDone(cam)} />
      </Page>
    </div></OnTop>
  );
}

// The last tile in a row of camera choices: opens the page above. `camera` is the hand-made framing when it is the one
// chosen (the tile then shows it); `onPick` gets the new one.
export function FreeFrameTile({ ep, t, clock, camera, onPick }: {
  ep: TimelineEpisode; t: number; clock: number; camera?: Camera; onPick: (camera: Camera) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button role="radio" aria-checked={!!camera} className={`tl-cam${camera ? " on" : ""}`} onClick={() => setOpen(true)}>
        {camera
          ? <div className="frame-box tl-cam-frame"><FrameSvg id="tl-freecam" boil={false} frame={frameWith(ep, t, clock, camera)} /></div>
          : <div className="tl-cam-new"><Icon name="move" size={28} /></div>}
        <span>{camera ? "Your framing" : "Frame it yourself"}</span>
      </button>
      {open && <FreeFramePage ep={ep} t={t} initial={camera ?? WHOLE} onDone={c => { setOpen(false); onPick(c); }} />}
    </>
  );
}
